<template>
  <div
    class="sf-dropdown2-item"
    :class="dropdownItemClasses"
    data-testid="dropdown-item"
    :data-submenu-trigger="submenu"
    :aria-haspopup="submenu ? true : undefined"
    :aria-expanded="submenu ? activeSubmenu === submenu : undefined"
    v-bind="$attrs"
    @click.stop.prevent="handleClick"
  >
    <span class="tw-inline-flex tw-flex-1">
      <slot name="icon" />
      <slot name="default" />
    </span>
    <!-- self-center: the item is a stretch-aligned flex row, so on multi-line
         items the glyph would otherwise ride along the top edge. -->
    <span
      v-if="hasCheckmark"
      class="sf-pad-l-1 fa fa-check tw-self-center"
    ></span>
    <span
      v-if="submenu"
      class="sf-pad-l-1 fa fa-chevron-right tw-self-center"
    ></span>
  </div>
</template>
<script setup lang="ts">
import { computed, inject, ref } from 'vue'

import { ACTIVE_SUBMENU } from '@app/shared/vue/composables/use-dropdown-submenu'

defineOptions({
  name: 'SfDropdownItem',
})

interface DropdownProps {
  hasCheckmark?: boolean
  hasBorder?: boolean
  isDisabled?: boolean
  isSelected?: boolean
  isRounded?: boolean
  isShort?: boolean
  isTall?: boolean
  isWide?: boolean
  isWrapped?: boolean
  href?: string
  // Marks the item as the trigger row of the dropdown's submenu-<id> panel:
  // renders the chevron and the data-submenu-trigger attribute the dropdown's
  // submenu handling keys on.
  submenu?: string
}

const props = withDefaults(defineProps<DropdownProps>(), {
  href: undefined,
  isWrapped: true,
  hasBorder: true,
  submenu: undefined,
})

const emit =
  defineEmits<{
    click: [e: Event]
  }>()

// Open state of the submenu this row triggers, provided by SfDropdown.
const activeSubmenu = inject(ACTIVE_SUBMENU, ref(null))

const dropdownItemClasses = computed(() => {
  return {
    'sf-dropdown2-item--tall': props.isTall,
    'sf-disabled tw-pointer-events-auto tw-cursor-default': props.isDisabled,
    'sf-pointer': !props.isDisabled,
    'sf-nowrap': !props.isWrapped,
    'sf-dropdown2-item--no-border': !props.hasBorder,
    'sf-dropdown2-item--selected': props.isSelected,
    'tw-py-2 tw-px-4': props.isShort,
    'tw-py-4 tw-px-6': props.isWide && props.isTall,
    'tw-rounded': props.isRounded,
  }
})

const handleClick = (e: Event) => {
  if (props.isDisabled) {
    return
  }
  emit('click', e)

  if (props.href) {
    window.Turbolinks.visit(props.href)
  }
}
</script>
